import { useMemo, useState } from "react";
import { Box, Button, Paper, Skeleton, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
	CheckCircleOutlineRounded,
	ErrorOutlineRounded,
	ReplayRounded,
	TimerOutlined,
	WarningAmberRounded,
} from "@mui/icons-material";
import { useCentralAlertas } from "../context";
import AnalisePadrao from "../analisePadrao";
import ModalNewTicket from "../../tickets/modalNewTicket";
import ModalNewTicketIxc from "../../tickets/modalNewTicketIxc";
import ModalCriarOs from "../modalCriarOs";
import { ERP_ATIVO, ERPS_COM_TICKET } from "../config/formTicketParams";
import { CATEGORIAS_INTELIGENTES } from "../../../utils/centralDeAlertas/labels";
import { corCategoriaInteligente } from "../../../utils/centralDeAlertas/cores";
import useStyles from "./styles";
import { GUIDES_CENTRAL, MetricInfo } from "../metricInfo";

// Alertas inteligentes: lista vertical de detecções automáticas (zona morta,
// OS faltante, falha recorrente) — uma linha por equipamento afetado, com
// contexto curto e ação. Todas as ações são reais e passam por um modal de
// confirmação antes de tocar o ERP:
// - falha recorrente -> AnalisePadrao (só leitura: histórico de 30 dias);
// - zona morta       -> ModalNewTicket, o modal padrão do sistema (cria
//                       atendimento no ERP, com cliente/serviço pré-selecionados);
// - OS faltante      -> ModalCriarOs (cria OS vinculada ao atendimento).
// Nenhuma delas escreve no Zabbix.

const QUANTIDADE_SKELETONS = 4;

// A ação de OS faltante usa o ModalCriarOs, que tem formulário por ERP
// (HubSoft via paramsNewOs; IXC via paramsNewTicket + funcionários por
// setor). VERO é somente leitura (sem criação de demanda/OS na API) e
// VOALLE/BRILHA não têm integração de escrita — o backend rejeita com 400
// (utils/erp/capacidadesErp.js); aqui o botão nem habilita.
const ERPS_COM_OS_CENTRAL = ["HUBSOFT", "IXC"];

const TOOLTIP_ACAO_INDISPONIVEL = {
	zona_morta: (erp) =>
		`O ERP ${erp} não suporta abertura de atendimento pelo sistema`,
	os_faltante: (erp) => `O ERP ${erp} não suporta criação de OS pelo sistema`,
};

// "SLA em risco" está fora da lista por enquanto: o único SLA que existe hoje
// é o por ETAPA do fluxo de atendimento (tela /interno/suporte/atendimentos,
// modal "Configuração de SLA por etapa"), que mede tempo na coluna do board —
// semântica diferente do SLA contratado que esta categoria pressupõe. O
// backend também não a detecta (ver incidentesEventos.js). Para religar:
// remover o filtro abaixo — rótulo, cor, ícone e ação já existem.
const CATEGORIAS_EXIBIDAS = CATEGORIAS_INTELIGENTES.filter(
	(categoria) => categoria !== "sla_em_risco"
);

const ICONE_CATEGORIA = {
	zona_morta: WarningAmberRounded,
	os_faltante: ErrorOutlineRounded,
	falha_recorrente: ReplayRounded,
	sla_em_risco: TimerOutlined,
};

const TITULO_CATEGORIA = {
	zona_morta: (nomeEquipamento) => `${nomeEquipamento} sem atendimento`,
	os_faltante: (nomeEquipamento) => `${nomeEquipamento} sem OS`,
	falha_recorrente: (nomeEquipamento) => `${nomeEquipamento} — falha recorrente`,
	sla_em_risco: (nomeEquipamento) => `${nomeEquipamento} — SLA em risco`,
};

const ACAO_CATEGORIA = {
	zona_morta: {
		rotulo: "Abrir atendimento",
		mensagem: (nomeEquipamento) =>
			`Abertura de atendimento sugerida para ${nomeEquipamento}`,
	},
	os_faltante: {
		rotulo: "Criar OS",
		mensagem: (nomeEquipamento) =>
			`Criação de OS sugerida para ${nomeEquipamento}`,
	},
	falha_recorrente: {
		rotulo: "Analisar padrão",
		mensagem: (nomeEquipamento) =>
			`Análise de padrão iniciada para ${nomeEquipamento}`,
	},
	sla_em_risco: {
		rotulo: "Escalar SLA",
		mensagem: (nomeEquipamento) => `SLA escalado para ${nomeEquipamento}`,
	},
};

// Texto de apoio da linha, no formato "Contexto: valor • complemento".
function descreverDeteccao({ categoria, incidente, alertas }) {
	const cliente = incidente.cliente?.nome || "—";
	const idAlerta = (incidente.alerta?.id || "—").toUpperCase();
	switch (categoria) {
		case "zona_morta":
			return `Cliente: ${cliente} • Alerta: ${idAlerta}`;
		case "os_faltante":
			return `Atendimento: ${(
				incidente.atendimento?.id || "—"
			).toUpperCase()} • Sem OS vinculada`;
		case "falha_recorrente":
			return `Cliente: ${cliente} • ${alertas} alertas no período`;
		case "sla_em_risco":
			return `Cliente: ${cliente} • Alerta: ${idAlerta}`;
		default:
			return `Cliente: ${cliente}`;
	}
}

export default function AlertasInteligentes() {
	const theme = useTheme();
	const styles = useStyles(theme);
	const { carregando, incidentesFiltrados, mostrarToast } = useCentralAlertas();
	// Incidente cuja falha recorrente está sendo analisada no modal
	// (null = modal fechado).
	const [incidenteAnalisado, setIncidenteAnalisado] = useState(null);
	// Incidente para o qual o operador está abrindo atendimento (zona morta).
	const [incidenteAtendimento, setIncidenteAtendimento] = useState(null);
	// Incidente para o qual o operador está criando OS (OS faltante).
	const [incidenteOs, setIncidenteOs] = useState(null);

	const deteccoes = useMemo(() => {
		const itens = [];
		CATEGORIAS_EXIBIDAS.forEach((categoria) => {
			const incidentes = incidentesFiltrados.filter((incidente) =>
				incidente.categoriasInteligentes.includes(categoria)
			);
			if (categoria === "falha_recorrente") {
				// Vários alertas do mesmo equipamento viram uma única linha,
				// com a contagem de alertas no texto de apoio.
				const porEquipamento = new Map();
				incidentes.forEach((incidente) => {
					const equipamentoId = incidente.alerta.equipamentoId;
					const existente = porEquipamento.get(equipamentoId);
					if (existente) existente.alertas += 1;
					else porEquipamento.set(equipamentoId, { categoria, incidente, alertas: 1 });
				});
				itens.push(...porEquipamento.values());
				return;
			}
			incidentes.forEach((incidente) => itens.push({ categoria, incidente }));
		});
		return itens;
	}, [incidentesFiltrados]);

	// Ações que criam registro no ERP habilitam conforme o ERP ativo — mesmo
	// espelho dos gates do backend (utils/erp/capacidadesErp.js). Ações de
	// leitura (analisar padrão) valem para todos os ERPs.
	const erp = ERP_ATIVO();
	const acaoDisponivel = {
		zona_morta: ERPS_COM_TICKET.includes(erp),
		os_faltante: ERPS_COM_OS_CENTRAL.includes(erp),
		falha_recorrente: true,
		sla_em_risco: true,
	};

	// Cada ação abre seu próprio modal — as que criam registro no ERP
	// (atendimento/OS) nunca disparam direto do clique na lista.
	const executarAcao = (categoria, incidente, nomeEquipamento) => {
		if (!acaoDisponivel[categoria]) return;
		if (categoria === "falha_recorrente") {
			setIncidenteAnalisado(incidente);
			return;
		}
		if (categoria === "zona_morta") {
			setIncidenteAtendimento(incidente);
			return;
		}
		if (categoria === "os_faltante") {
			setIncidenteOs(incidente);
			return;
		}
		const acao = ACAO_CATEGORIA[categoria];
		console.log(`[central-de-alertas] ${acao.rotulo}`, incidente);
		mostrarToast(acao.mensagem(nomeEquipamento));
	};

	return (
		<Paper sx={styles.container}>
			<Box sx={styles.linhaCabecalho}>
				<Box sx={styles.cabecalho}>
					<Typography variant="mySubtitle">Alertas inteligentes</Typography>
					<Typography variant="caption" sx={styles.caption}>
						Detecções automáticas de lacunas na operação
					</Typography>
				</Box>
				<MetricInfo theme={theme} guide={GUIDES_CENTRAL.alertasInteligentes} />
			</Box>
			{carregando ? (
				<Box sx={styles.lista}>
					{Array.from({ length: QUANTIDADE_SKELETONS }, (_, indice) => (
						<Skeleton key={indice} variant="rounded" sx={styles.skeletonItem} />
					))}
				</Box>
			) : deteccoes.length === 0 ? (
				<Box sx={styles.estadoVazio}>
					<CheckCircleOutlineRounded sx={styles.iconeVazio} />
					<Typography variant="caption" sx={styles.textoVazio}>
						Nenhuma detecção automática no período
					</Typography>
				</Box>
			) : (
				<Box sx={styles.lista}>
					{deteccoes.map((deteccao) => {
						const { categoria, incidente } = deteccao;
						const cor = corCategoriaInteligente(theme, categoria);
						const Icone = ICONE_CATEGORIA[categoria];
						const nomeEquipamento = incidente.equipamento?.nome || "—";

						return (
							<Box key={`${categoria}-${incidente.id}`} sx={styles.itemLista(cor)}>
								<Icone sx={styles.iconeCategoria(cor)} />
								<Box sx={styles.textosItem}>
									<Typography sx={styles.tituloItem}>
										{TITULO_CATEGORIA[categoria](nomeEquipamento)}
									</Typography>
									<Typography variant="caption" sx={styles.descricaoItem}>
										{descreverDeteccao(deteccao)}
									</Typography>
								</Box>
								{acaoDisponivel[categoria] ? (
									<Button
										size="small"
										onClick={() =>
											executarAcao(categoria, incidente, nomeEquipamento)
										}
										sx={styles.botaoAcao}
									>
										{ACAO_CATEGORIA[categoria].rotulo}
									</Button>
								) : (
									// span obrigatório: Tooltip do MUI não dispara em
									// elemento disabled.
									<Tooltip title={TOOLTIP_ACAO_INDISPONIVEL[categoria]?.(erp) || ""}>
										<span>
											<Button size="small" disabled sx={styles.botaoAcao}>
												{ACAO_CATEGORIA[categoria].rotulo}
											</Button>
										</span>
									</Tooltip>
								)}
							</Box>
						);
					})}
				</Box>
			)}
			<AnalisePadrao
				aberto={Boolean(incidenteAnalisado)}
				equipamento={incidenteAnalisado?.equipamento}
				cliente={incidenteAnalisado?.cliente}
				onFechar={() => setIncidenteAnalisado(null)}
			/>
			{/* Abertura de atendimento usa o MODAL DO SISTEMA (o mesmo de
			    /interno/suporte/*), que já pré-seleciona cliente e serviço pelas
			    props e carrega todo o fluxo padrão (validações, OS junto,
			    anexos). A Central só informa de qual alerta veio. O modal é o
			    do ERP ativo (mesmo branch das telas de tickets); ERPs sem
			    criação nem chegam aqui — a ação fica desabilitada. */}
			{incidenteAtendimento && erp === "HUBSOFT" && (
				<ModalNewTicket
					open
					handleClose={() => setIncidenteAtendimento(null)}
					refrashTIckets={() => {
						setIncidenteAtendimento(null);
						mostrarToast("Atendimento aberto no ERP.");
					}}
					clienteErpId={incidenteAtendimento?.alerta?.clienteErpId ?? null}
					serviceId={
						incidenteAtendimento?.alerta?.servicoId
							? Number(incidenteAtendimento.alerta.servicoId)
							: null
					}
				/>
			)}
			{incidenteAtendimento && erp === "IXC" && (
				<ModalNewTicketIxc
					open
					handleClose={() => setIncidenteAtendimento(null)}
					refrashTIckets={() => {
						setIncidenteAtendimento(null);
						mostrarToast("Atendimento aberto no ERP.");
					}}
					clienteErpId={incidenteAtendimento?.alerta?.clienteErpId ?? null}
					serviceId={
						incidenteAtendimento?.alerta?.servicoId
							? Number(incidenteAtendimento.alerta.servicoId)
							: null
					}
				/>
			)}
			<ModalCriarOs
				aberto={Boolean(incidenteOs)}
				incidente={incidenteOs}
				onFechar={() => setIncidenteOs(null)}
				onSucesso={(os) => {
					setIncidenteOs(null);
					mostrarToast(
						`OS ${os?.id_ordem_servico ? `#${os.id_ordem_servico} ` : ""}criada no ERP.`,
					);
				}}
			/>
		</Paper>
	);
}
